import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  HistoryIcon,
  WorkflowIcon,
} from "lucide-react";
import { Tooltip } from "../ui/tooltip";
import moment from "moment";
import { WorkflowRun } from "../../services/WorkflowService";
import { cn } from "../../lib/utils";

function formatWorkflowDuration(
  profile:
    | { spans?: { label?: string; start_ms?: number; end_ms?: number }[]; total_ms?: number }
    | null
    | undefined
): string {
  const raw = profile?.spans;
  const workerExec = raw?.find((s) => s.label === "worker_execution");
  if (workerExec?.start_ms != null && workerExec?.end_ms != null) {
    return `${Math.round(workerExec.end_ms - workerExec.start_ms)}ms`;
  }
  if (profile?.total_ms != null && Number(profile.total_ms) > 0) {
    return `${Math.round(Number(profile.total_ms))}ms`;
  }
  return "-";
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

interface WorkflowRunDetailModalProps {
  run: WorkflowRun | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewSession?: () => void;
}

const WorkflowRunDetailModal: React.FC<WorkflowRunDetailModalProps> = ({
  run,
  open,
  onOpenChange,
  onViewSession,
}) => {
  const [profileExpandedKeys, setProfileExpandedKeys] = useState<Set<string>>(
    new Set()
  );

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) setProfileExpandedKeys(new Set());
  };

  const showViewSession = run?.session_id && onViewSession;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <WorkflowIcon className="w-5 h-5 text-maitai-green" />
            Workflow Run Details
          </DialogTitle>
        </DialogHeader>
        {run && (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-gc-med text-foreground">
                    {run.workflow_name || run.workflow_id}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    <span
                      className={cn(
                        "font-medium",
                        run.status === "COMPLETED" &&
                          "text-green-600 dark:text-green-400",
                        run.status === "FAILED" &&
                          "text-red-600 dark:text-red-400",
                        run.status === "TIMEOUT" &&
                          "text-orange-600 dark:text-orange-400"
                      )}
                    >
                      {run.status}
                    </span>
                    {" · "}
                    {formatWorkflowDuration(run.profile)}
                    {run.date_created && (
                      <>
                        {" · "}
                        {moment(run.date_created).format("MMM D, YYYY h:mm A")}
                      </>
                    )}
                  </p>
                </div>
                {showViewSession && onViewSession && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={onViewSession}
                  >
                    <HistoryIcon className="w-3.5 h-3.5" />
                    View Session
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="font-mono truncate max-w-[280px]"
                  title={run.session_id}
                >
                  {run.session_id}
                </span>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-muted hover:text-foreground transition-colors shrink-0"
                  onClick={() => copyToClipboard(run.session_id || "")}
                  aria-label="Copy session ID"
                >
                  <CopyIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
            {run.error_message && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-400">
                  Error
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {run.error_message}
                </p>
              </div>
            )}
            {run.profile?.spans &&
              Array.isArray(run.profile.spans) &&
              (() => {
                const rawSpans = run.profile.spans as {
                  label?: string;
                  start_ms?: number;
                  end_ms?: number;
                  depth?: number;
                  trace_type?: string;
                }[];
                const workflowSpans = rawSpans.filter(
                  (s) => s.trace_type === "workflow"
                );
                if (workflowSpans.length === 0) {
                  return (
                    <div>
                      <h4 className="text-sm font-gc-med text-foreground mb-3">
                        Execution Profile
                      </h4>
                      <div className="rounded-md bg-muted/50 border border-border px-3 py-4 text-sm text-muted-foreground">
                        No workflow traces found
                      </div>
                    </div>
                  );
                }

                const minStart = Math.min(
                  ...workflowSpans.map((s) => s.start_ms ?? 0)
                );
                const maxEnd = Math.max(
                  ...workflowSpans.map((s) => s.end_ms ?? s.start_ms ?? 0)
                );
                const rangeMs = Math.max(1, maxEnd - minStart);
                const rangeStart = minStart;

                const userSpans = rawSpans.filter(
                  (s) =>
                    s.trace_type === "workflow" || s.trace_type === "context"
                );
                const spans = userSpans.map((s) => ({
                  ...s,
                  depth: s.trace_type === "workflow" ? 0 : 1,
                }));

                const sortedSpans = [...spans].sort(
                  (a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0)
                );
                const laneHeight = 24;
                const indentPerDepth = 14;

                const spanKey = (i: number) =>
                  `${sortedSpans[i].label ?? ""}-${sortedSpans[i].start_ms ?? 0}`;
                const parentOf = (i: number): number => {
                  const s = sortedSpans[i];
                  const d = s.depth ?? 0;
                  if (d === 0) return -1;
                  const candidates = sortedSpans
                    .map((p, j) => ({ p, j }))
                    .filter(({ p }) => (p.depth ?? 0) === d - 1);
                  const ps = s.start_ms ?? 0;
                  const pe = s.end_ms ?? ps;
                  const containing = candidates.filter(
                    ({ p }) =>
                      (p.start_ms ?? 0) <= ps && (p.end_ms ?? 0) >= pe
                  );
                  if (containing.length === 0) return -1;
                  containing.sort((a, b) => {
                    const aRange =
                      (a.p.end_ms ?? 0) - (a.p.start_ms ?? 0);
                    const bRange =
                      (b.p.end_ms ?? 0) - (b.p.start_ms ?? 0);
                    return aRange - bRange;
                  });
                  return containing[0].j;
                };
                const childrenOf = (i: number) =>
                  sortedSpans
                    .map((_, j) => (parentOf(j) === i ? j : -1))
                    .filter((j) => j >= 0);

                const isVisible = (i: number): boolean => {
                  const d = sortedSpans[i].depth ?? 0;
                  if (d === 0) return true;
                  const p = parentOf(i);
                  if (p < 0) return false;
                  return isVisible(p) && profileExpandedKeys.has(spanKey(p));
                };
                const visibleIndices = sortedSpans
                  .map((_, i) => i)
                  .filter(isVisible);

                const toggleExpand = (key: string) => {
                  setProfileExpandedKeys((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                };

                return (
                  <div>
                    <h4 className="text-sm font-gc-med text-foreground mb-3">
                      Execution Profile
                    </h4>
                    <div className="relative rounded-md bg-muted/50 border border-border px-3 py-4 overflow-visible">
                      <div
                        className="flex gap-4"
                        style={{
                          minHeight: `${visibleIndices.length * laneHeight + 20}px`,
                        }}
                      >
                        <div className="flex-shrink-0 w-44 space-y-0.5">
                          {visibleIndices.map((idx, rowIdx) => {
                            const span = sortedSpans[idx];
                            const key = spanKey(idx);
                            const hasChildren = childrenOf(idx).length > 0;
                            const isExpanded =
                              profileExpandedKeys.has(key);
                            return (
                              <div
                                key={idx}
                                className="h-6 flex items-center gap-0.5"
                              >
                                {hasChildren ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleExpand(key)}
                                    className="flex-shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                    aria-label={
                                      isExpanded ? "Collapse" : "Expand"
                                    }
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="w-3.5 h-3.5" />
                                    ) : (
                                      <ChevronRightIcon className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                ) : (
                                  <span className="w-4 flex-shrink-0" />
                                )}
                                <span
                                  className="text-xs text-foreground truncate flex-1 min-w-0"
                                  style={{
                                    paddingLeft: `${(span.depth ?? 0) * indentPerDepth}px`,
                                  }}
                                  title={span.label}
                                >
                                  {span.label || "?"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex-1 min-w-0 relative">
                          <div
                            className="relative"
                            style={{
                              height: `${visibleIndices.length * laneHeight + 20}px`,
                            }}
                          >
                            {visibleIndices.map((idx, rowIdx) => {
                              const span = sortedSpans[idx];
                              const start = span.start_ms ?? 0;
                              const end = span.end_ms ?? start;
                              const left =
                                ((start - rangeStart) / rangeMs) * 100;
                              const width = Math.max(
                                0.5,
                                ((end - start) / rangeMs) * 100
                              );
                              const durationMs = Math.round(end - start);
                              const depthColors = [
                                "bg-blue-500/70",
                                "bg-emerald-500/70",
                                "bg-amber-500/70",
                                "bg-purple-500/70",
                              ];
                              const barColor =
                                depthColors[
                                  (span.depth ?? 0) % depthColors.length
                                ];
                              return (
                                <Tooltip
                                  key={idx}
                                  content={`${span.label}: ${durationMs}ms`}
                                  immediate
                                  side="bottom"
                                >
                                  <div
                                    className={cn(
                                      "absolute h-5 rounded cursor-default hover:opacity-90 transition-opacity",
                                      barColor
                                    )}
                                    style={{
                                      left: `${left}%`,
                                      width: `${width}%`,
                                      top: rowIdx * laneHeight + 2,
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </div>
                          <div className="flex justify-between mt-1 text-[10px] font-mono text-muted-foreground">
                            <span>0ms</span>
                            <span>{Math.round(rangeMs)}ms</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 w-14 text-right space-y-0.5">
                          {visibleIndices.map((idx) => {
                            const span = sortedSpans[idx];
                            const duration =
                              span.start_ms != null && span.end_ms != null
                                ? Math.round(span.end_ms - span.start_ms)
                                : 0;
                            return (
                              <div
                                key={idx}
                                className="h-6 flex items-center justify-end"
                              >
                                <span className="text-xs font-mono text-muted-foreground">
                                  {duration}ms
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border">
                <span className="text-xs font-gc-med text-foreground">
                  Input
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify(run.input, null, 2) || "{}"
                    )
                  }
                >
                  <CopyIcon className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <pre className="text-xs font-mono p-3 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words bg-background">
                {JSON.stringify(run.input, null, 2) || "{}"}
              </pre>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border">
                <span className="text-xs font-gc-med text-foreground">
                  Output
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    const out = run.output;
                    if (!out) {
                      copyToClipboard("{}");
                      return;
                    }
                    const content = (out as { content?: string }).content;
                    if (
                      typeof content === "string" &&
                      content.trim().startsWith("{")
                    ) {
                      try {
                        copyToClipboard(
                          JSON.stringify(JSON.parse(content), null, 2)
                        );
                      } catch {
                        copyToClipboard(content);
                      }
                    } else {
                      copyToClipboard(JSON.stringify(out, null, 2));
                    }
                  }}
                >
                  <CopyIcon className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <pre className="text-xs font-mono p-3 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words bg-background">
                {(() => {
                  const out = run.output;
                  if (!out) return "{}";
                  const content = (out as { content?: string }).content;
                  if (
                    typeof content === "string" &&
                    content.trim().startsWith("{")
                  ) {
                    try {
                      const parsed = JSON.parse(content);
                      return JSON.stringify(parsed, null, 2);
                    } catch {
                      return content;
                    }
                  }
                  return JSON.stringify(out, null, 2);
                })()}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowRunDetailModal;
export { formatWorkflowDuration };
